import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ACCREDITATION, STORAGE_BUCKETS } from '@bcc/shared';
import { apiFetch } from '../lib/api';
import { profileDisplayName, profileInitials } from '../lib/profileDisplay';
import { supabase } from '../lib/supabase';
import { SITE_FILE_CONTROL, SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../constants/siteUi';
import { useAuth } from '../contexts/AuthContext';

export function Account() {
  const { session, profile, refreshProfile } = useAuth();
  const token = session?.access_token;

  const [fullName, setFullName] = useState(profile?.full_name ?? '');
  const [accreditation, setAccreditation] = useState(profile?.accreditation_status ?? ACCREDITATION.UNKNOWN);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);

  useEffect(() => {
    setFullName(profile?.full_name ?? '');
    setAccreditation(profile?.accreditation_status ?? ACCREDITATION.UNKNOWN);
    setImgBroken(false);
  }, [profile?.full_name, profile?.accreditation_status, profile?.avatar_url]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setMsg(null);
    setErr(null);
    setSaving(true);
    try {
      await apiFetch('/api/me', {
        method: 'PATCH',
        token,
        body: JSON.stringify({
          full_name: fullName.trim() || null,
          accreditation_status: accreditation,
        }),
      });
      await refreshProfile();
      setMsg('Saved.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onPickAvatar(file: File | null) {
    if (!file || !token || !profile) return;
    setErr(null);
    setMsg(null);
    setUploading(true);
    try {
      const raw = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const ext = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(raw) ? raw : 'jpg';
      const path = `${profile.id}/avatar.${ext}`;
      const { error: upErr } = await supabase.storage.from(STORAGE_BUCKETS.AVATARS).upload(path, file, {
        upsert: true,
        contentType: file.type || 'image/jpeg',
      });
      if (upErr) throw new Error(upErr.message);

      const { data: urlData } = supabase.storage.from(STORAGE_BUCKETS.AVATARS).getPublicUrl(path);
      const publicUrl = urlData.publicUrl;

      await apiFetch('/api/me', {
        method: 'PATCH',
        token,
        body: JSON.stringify({ avatar_url: publicUrl }),
      });
      await refreshProfile();
      setImgBroken(false);
      setMsg('Profile photo updated.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  async function clearAvatar() {
    if (!token) return;
    setErr(null);
    setMsg(null);
    setUploading(true);
    try {
      await apiFetch('/api/me', {
        method: 'PATCH',
        token,
        body: JSON.stringify({ avatar_url: null }),
      });
      await refreshProfile();
      setImgBroken(false);
      setMsg('Profile photo removed.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  const name = profileDisplayName(profile);
  const initials = profileInitials(profile);

  return (
    <div className="mx-auto max-w-xl px-4 py-16">
      <div className="mb-10">
        <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">Account</h1>
        <p className="mt-2 text-sm text-[var(--home-muted-on-dark)]">Profile photo and settings apply wherever you are signed in.</p>
      </div>

      <div className={`space-y-8 p-6 ${SITE_GLASS_CARD}`}>
        <div className="flex flex-wrap items-center gap-6">
          {profile?.avatar_url && !imgBroken ? (
            <img
              src={profile.avatar_url}
              alt=""
              className="h-24 w-24 rounded-full border border-white/[0.12] object-cover ring-1 ring-white/[0.06]"
              onError={() => setImgBroken(true)}
            />
          ) : (
            <span className="flex h-24 w-24 items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.08] text-xl font-semibold text-[var(--home-text-on-dark)] ring-1 ring-white/[0.06]">
              {initials}
            </span>
          )}
          <div className="min-w-0 flex-1 space-y-3">
            <label className="block text-sm">
              <span className="text-[var(--home-muted-on-dark)]">Profile photo</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                disabled={uploading || !token}
                onChange={(e) => void onPickAvatar(e.target.files?.[0] ?? null)}
                className={`mt-1 block w-full ${SITE_FILE_CONTROL}`}
              />
            </label>
            {profile?.avatar_url ? (
              <button
                type="button"
                disabled={uploading}
                onClick={() => void clearAvatar()}
                className="text-xs text-rose-300/95 underline-offset-2 transition hover:text-rose-200 hover:underline disabled:opacity-50"
              >
                Remove photo
              </button>
            ) : null}
            {uploading ? <p className="text-xs text-[var(--home-muted-on-dark)]">Working…</p> : null}
          </div>
        </div>

        <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
          <p className="text-sm font-medium text-[var(--home-text-on-dark)]">{name}</p>
          {profile?.email ? (
            <p className="text-xs text-[var(--home-muted-on-dark)]">Signed in as {profile.email}</p>
          ) : null}

          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Display / legal name</span>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>

          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Accreditation</span>
            <select
              value={accreditation}
              onChange={(e) => setAccreditation(e.target.value as typeof accreditation)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            >
              <option value={ACCREDITATION.UNKNOWN}>Prefer not to say</option>
              <option value={ACCREDITATION.ACCREDITED}>Accredited</option>
              <option value={ACCREDITATION.NON_ACCREDITED}>Not accredited</option>
            </select>
          </label>

          {err ? <p className="text-sm text-rose-300">{err}</p> : null}
          {msg ? <p className="text-sm text-emerald-300/95">{msg}</p> : null}

          <button
            type="submit"
            disabled={saving || !token}
            className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] shadow-md shadow-black/20 transition hover:brightness-105 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </form>
      </div>
    </div>
  );
}
